import React, { useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import { getCurrencySymbol } from '../utils/formatPrice';
import './DiscountValuePad.css';

/** Named discount configured server-side (loaded from /Discount/get-active). */
interface DiscountPreset {
    id: string;
    name: string;
    description?: string;
    discountType: string;
    value: number;
}

interface DiscountValuePadProps {
    title?: string;
    subtitle?: string;
    initialValue?: number | string;
    initialType?: string;
    /** Pre-selected named discount (matches a preset from /Discount/get-active). */
    initialName?: string;
    /** Discountable base used to preview "new total". Omit to hide the running total. */
    baseAmount?: number;
    applyLabel?: string;
    onClose: () => void;
    onSubmit: (value: number, discountType: string, discountName?: string) => void;
}

const PERCENT_PRESETS = ['5', '10', '15', '20', '25', '50'];
const AMOUNT_PRESETS = ['1', '2', '5', '10', '20'];

const DiscountValuePad: React.FC<DiscountValuePadProps> = ({
    title = 'Add discount',
    subtitle,
    initialValue,
    initialType,
    initialName,
    baseAmount,
    applyLabel = 'Apply',
    onClose,
    onSubmit,
}) => {
    const sym = getCurrencySymbol();
    const startType = initialType === 'percentage' ? 'percentage' : 'amount';
    const startValue =
        initialValue !== undefined && initialValue !== null && String(initialValue) !== '0'
            ? String(initialValue)
            : '';

    const [type, setType] = useState<string>(startType);
    const [value, setValue] = useState<string>(startValue);
    // If an existing value isn't one of the presets, start in custom mode so it stays editable.
    const presetsForStart = startType === 'percentage' ? PERCENT_PRESETS : AMOUNT_PRESETS;
    const [custom, setCustom] = useState<boolean>(!!startValue && !presetsForStart.includes(startValue));

    // Named server-side discounts. Selecting one locks the keypad and supplies its
    // value/type/name — preserving the legacy "preset discount" behaviour.
    const [namedPresets, setNamedPresets] = useState<DiscountPreset[]>([]);
    const [selectedPresetId, setSelectedPresetId] = useState<string>('');
    const [discountName, setDiscountName] = useState<string>(initialName || '');

    // A named preset is selected → keypad is locked and value/type/name come from it.
    const isPreset = selectedPresetId !== '';

    useEffect(() => {
        (async () => {
            try {
                const res = await fetch(`${process.env.REACT_APP_API_URL}/Discount/get-active`, { credentials: 'include' });
                if (res.ok) {
                    const data: DiscountPreset[] = await res.json();
                    setNamedPresets(data || []);
                    if (initialName) {
                        const match = (data || []).find(d => d.name === initialName);
                        if (match) {
                            setSelectedPresetId(match.id);
                            setType(match.discountType === 'percentage' ? 'percentage' : 'amount');
                            setValue(String(match.value ?? ''));
                            setCustom(false);
                        }
                    }
                }
            } catch (e) {
                console.warn('[Discount] Failed to load presets', e);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handlePresetChange = (id: string) => {
        setSelectedPresetId(id);
        if (!id) {
            setDiscountName('');
            return;
        }
        const preset = namedPresets.find(p => p.id === id);
        if (preset) {
            setType(preset.discountType === 'percentage' ? 'percentage' : 'amount');
            setValue(String(preset.value ?? ''));
            setCustom(false);
            setDiscountName(preset.name || '');
        }
    };

    const presets = type === 'percentage' ? PERCENT_PRESETS : AMOUNT_PRESETS;

    const num = useMemo(() => {
        const n = parseFloat(value);
        return isNaN(n) ? 0 : n;
    }, [value]);

    const discAmount = useMemo(() => {
        if (num <= 0) return 0;
        if (type === 'percentage') {
            return baseAmount !== undefined ? (baseAmount * num) / 100 : 0;
        }
        return num;
    }, [num, type, baseAmount]);

    const money = (n: number) => `${sym}${n.toFixed(2)}`;

    const switchType = (next: string) => {
        if (isPreset) return;
        setType(next);
        setValue('');
        setCustom(false);
    };

    const apply = () => {
        if (num <= 0) return;
        onSubmit(num, type, discountName || undefined);
    };

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
            if (e.key === 'Enter') apply();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [num, type, discountName]);

    let resultText: React.ReactNode = 'Pick a discount above';
    if (num > 0) {
        if (baseAmount !== undefined) {
            resultText = (
                <>
                    −{money(discAmount)} off · new total <b>{money(Math.max(0, baseAmount - discAmount))}</b>
                </>
            );
        } else if (type === 'percentage') {
            resultText = <>{num}% off</>;
        } else {
            resultText = <>−{money(num)} off</>;
        }
    }

    const presetOptions = [
        { value: '', label: 'Manual entry' },
        ...namedPresets.map((p) => ({
            value: p.id,
            label: `${p.name} (${p.discountType === 'percentage' ? `${p.value}%` : `${sym}${p.value}`})`,
        })),
    ];
    const selectedOption = presetOptions.find(o => o.value === selectedPresetId) || presetOptions[0];

    return (
        <div className="dvp" role="dialog" aria-modal="true">
            <div className="dvp__head">
                <div>
                    <div className="dvp__title">{title}</div>
                    {subtitle && <div className="dvp__subtitle">{subtitle}</div>}
                </div>
                <button className="dvp__close" onClick={onClose} aria-label="Close">
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <path d="M13 1L1 13M1 1L13 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </button>
            </div>

            <div className="dvp__body">
                {namedPresets.length > 0 && (
                    <div className="dvp__named">
                        <label className="dvp__namedlabel">Preset discount</label>
                        <Select
                            isSearchable={false}
                            menuPlacement="auto"
                            placeholder="Manual entry"
                            options={presetOptions}
                            value={selectedOption}
                            onChange={(opt: any) => handlePresetChange(opt ? opt.value : '')}
                            theme={(theme) => ({ ...theme, colors: { ...theme.colors, primary: '#1643E0', primary25: '#E9EEFF' } })}
                            styles={{ menu: (base) => ({ ...base, zIndex: 2000 }) }}
                        />
                    </div>
                )}

                <div className="dvp__seg">
                    <button className={`dvp__segbtn${type === 'percentage' ? ' is-on' : ''}`} disabled={isPreset} onClick={() => switchType('percentage')}>Percent %</button>
                    <button className={`dvp__segbtn${type === 'amount' ? ' is-on' : ''}`} disabled={isPreset} onClick={() => switchType('amount')}>Amount {sym}</button>
                </div>

                <div className="dvp__presets">
                    {presets.map((p) => (
                        <button
                            key={p}
                            className={`dvp__preset${!custom && value === p ? ' is-on' : ''}`}
                            disabled={isPreset}
                            onClick={() => { setValue(p); setCustom(false); }}
                        >
                            {type === 'percentage' ? `${p}%` : `${sym}${p}`}
                        </button>
                    ))}
                    <button
                        className={`dvp__preset dvp__preset--custom${custom ? ' is-on' : ''}`}
                        disabled={isPreset}
                        onClick={() => { setCustom(true); setValue(''); }}
                    >
                        Custom
                    </button>
                </div>

                {custom && !isPreset && (
                    <div className="dvp__customfield">
                        {type === 'amount' && <span className="dvp__customunit">{sym}</span>}
                        <input
                            type="number"
                            inputMode="decimal"
                            autoFocus
                            min="0"
                            value={value}
                            onChange={(e) => setValue(e.target.value)}
                            placeholder={type === 'percentage' ? 'Enter percent' : 'Enter amount'}
                        />
                        {type === 'percentage' && <span className="dvp__customunit">%</span>}
                    </div>
                )}

                <div className={`dvp__result${num > 0 ? ' is-on' : ''}`}>{resultText}</div>
            </div>

            <div className="dvp__foot">
                <button className="dvp__btn dvp__btn--ghost" onClick={onClose}>Cancel</button>
                <button className="dvp__btn dvp__btn--primary" disabled={num <= 0} onClick={apply}>
                    {applyLabel}{discAmount > 0 ? ` −${money(discAmount)}` : type === 'percentage' && num > 0 ? ` ${num}%` : ''}
                </button>
            </div>
        </div>
    );
};

export default DiscountValuePad;
